import React from 'react';
import {StyleSheet, Text, TouchableOpacity} from 'react-native';
import {useTranslation} from 'react-i18next';
import PosBottomSheet from './PosBottomSheet';
import {priceFormatter} from '../../helpers/numbers';
import {useTheme} from '../../helpers/ThemeContext';
import {haptics} from '../../helpers/haptics';
import {POS_MIN_TOUCH} from '../../helpers/posLayout';

/**
 * Size / portion picker for a product that has several sizes: a bottom sheet with
 * one big row per size (amount + unit on the left, price on the right) instead of
 * the old centred dialog with small buttons. Shared by the grid tile and the list
 * row of the Sale screen.
 *
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {?object} props.food The product (needs `name` and `sizes`).
 * @param {function(object, number): void} props.onSelect Called with the chosen size and its index.
 * @param {function(): void} props.onClose Close without choosing.
 * @returns {JSX.Element}
 */
const SizePickerSheet = ({visible, food, onSelect, onClose}) => {
    const {t, i18n} = useTranslation();
    const {theme} = useTheme();
    const locale = i18n.language || 'hu';
    const name = typeof food?.name === 'object' ? (food?.name?.[locale] || food?.name?.hu || '') : (food?.name || '');

    return (
        <PosBottomSheet visible={visible} onClose={onClose} title={name} subtitle={t('multiple_sizes_available')}>
            {(food?.sizes || []).map((size, index) => (
                <TouchableOpacity
                    key={index}
                    activeOpacity={0.8}
                    accessibilityRole="button"
                    onPress={() => {
                        haptics.tap();
                        onSelect(size, index);
                    }}
                    style={[styles.row, {backgroundColor: theme.colors.inputBackground || theme.colors.background, borderColor: theme.colors.border}]}
                >
                    <Text style={[styles.label, {color: theme.colors.text}]} numberOfLines={1}>{`${size.amount} ${size.unit}`}</Text>
                    <Text style={styles.price}>{priceFormatter(size.price)}</Text>
                </TouchableOpacity>
            ))}
        </PosBottomSheet>
    );
};

const styles = StyleSheet.create({
    row: {
        minHeight: POS_MIN_TOUCH + 8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderWidth: StyleSheet.hairlineWidth,
        borderRadius: 16,
        paddingHorizontal: 18,
        marginBottom: 10,
    },
    label: {flex: 1, fontSize: 20, fontWeight: '800'},
    price: {fontSize: 20, fontWeight: '900', color: '#f76e24', marginLeft: 12},
});

export default SizePickerSheet;
